import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../services/api";
import publicAxiosInstance from "../services/publicApi";
import { API_ENDPOINTS } from "../constants/actionTypes";

// Thunk for fetching ALL branches (Protected)
export const fetchAllBranches = createAsyncThunk(
  "branch/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.BRANCH.SELECT_ALL);

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch branches",
      );
    }
  },
);

// Thunk for fetching ALL branches (Public - No Auth)
export const fetchAllBranchesPublic = createAsyncThunk(
  "branch/fetchAllPublic",
  async (_, { rejectWithValue }) => {
    try {
      const response = await publicAxiosInstance.get(
        API_ENDPOINTS.BRANCH.SELECT_ALL,
      );

      return response.data;
    } catch (error) {
      console.error(
        "❌ Public Branches API Error:",
        error.response?.status || "No Response",
        error.response?.data || error.message,
      );
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch public branches",
      );
    }
  },
);

// Thunk for fetching Branch Dropdown (Public - No Auth)
export const fetchBranchDropdownPublic = createAsyncThunk(
  "branch/fetchDropdownPublic",
  async (companyId, { rejectWithValue }) => {
    try {
      const response = await publicAxiosInstance.get(
        `${API_ENDPOINTS.BRANCH.DROPDOWN}?companyId=${companyId}`,
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch public branch dropdown",
      );
    }
  },
);

// Thunk for fetching branch by ID
export const fetchBranchById = createAsyncThunk(
  "branch/fetchById",
  async (branchId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(API_ENDPOINTS.BRANCH.SELECT, {
        branchId,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Branch not found",
      );
    }
  },
);

// Thunk for adding a new branch
export const addBranch = createAsyncThunk(
  "branch/add",
  async (branchData, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.post(
        API_ENDPOINTS.BRANCH.INSERT,
        branchData,
      );
      dispatch(fetchAllBranches());
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to add branch",
      );
    }
  },
);

// Thunk for updating an existing branch
export const updateBranch = createAsyncThunk(
  "branch/update",
  async (branchData, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.put(
        API_ENDPOINTS.BRANCH.UPDATE,
        branchData,
      );
      dispatch(fetchAllBranches()); // Refresh list after update
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update branch",
      );
    }
  },
);

// Thunk for deleting a branch
export const deleteBranch = createAsyncThunk(
  "branch/delete",
  async (branchId, { rejectWithValue, dispatch }) => {
    try {
      await axiosInstance.delete(
        `${API_ENDPOINTS.BRANCH.DELETE}?id=${branchId}`,
      );
      dispatch(fetchAllBranches());
      return branchId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete branch",
      );
    }
  },
);

const branchSlice = createSlice({
  name: "branch",
  initialState: {
    branchesList: [], // Flat array for table
    branchDropdown: [], // Dropdown array
    pagination: null, // Full pagination wrapper
    selectedBranch: null,
    isLoading: false,
    error: null,
  },
  reducers: {
    clearSelectedBranch: (state) => {
      state.selectedBranch = null;
    },
    clearBranchError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch All Branches (Protected)
      .addCase(fetchAllBranches.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllBranches.fulfilled, (state, action) => {
        state.isLoading = false;
        const apiData = action.payload?.data;
        state.branchesList = apiData?.data || apiData || [];
        state.pagination = apiData;
      })
      .addCase(fetchAllBranches.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch branches";
        console.error("❌ Branches API Failed:", action.payload);
      })
      // Fetch All Branches Public
      .addCase(fetchAllBranchesPublic.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllBranchesPublic.fulfilled, (state, action) => {
        state.isLoading = false;
        const apiData = action.payload?.data;
        state.branchesList = apiData?.data || apiData || [];
        state.pagination = apiData;
      })
      .addCase(fetchAllBranchesPublic.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch public branches";
        console.error("❌ Public Branches API Failed:", action.payload);
      })
      // Fetch Branch DropDown Public
      .addCase(fetchBranchDropdownPublic.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBranchDropdownPublic.fulfilled, (state, action) => {
        state.isLoading = false;
        const apiData = action.payload?.data;
        state.branchDropdown = apiData?.data || apiData || action.payload || [];
      })
      .addCase(fetchBranchDropdownPublic.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload || "Failed to fetch public branch dropdown";
      })
      // Fetch By ID
      .addCase(fetchBranchById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBranchById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedBranch = action.payload.data;
      })
      .addCase(fetchBranchById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Add Branch
      .addCase(addBranch.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addBranch.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(addBranch.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update Branch
      .addCase(updateBranch.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateBranch.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(updateBranch.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Delete Branch
      .addCase(deleteBranch.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteBranch.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(deleteBranch.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearSelectedBranch, clearBranchError } = branchSlice.actions;
export default branchSlice.reducer;
